import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell, X, Check, CheckCheck, Trash2, FileCheck, ClipboardCheck,
  Inbox, Award, User, Info, RefreshCcw, Shield, Volume2, VolumeX, MessageCircle
} from 'lucide-react';
import { playNotificationSound, isNotificationMuted, setNotificationMuted } from '@/lib/notifySound';

const ease = [0.16, 1, 0.3, 1];

const typeIcons = {
  certificate: { icon: FileCheck, color: '#00D387', bg: 'from-[#00D387]/15 to-transparent' },
  audit: { icon: ClipboardCheck, color: '#3B82F6', bg: 'from-[#3B82F6]/15 to-transparent' },
  application: { icon: Inbox, color: '#F59E0B', bg: 'from-[#F59E0B]/15 to-transparent' },
  renewal: { icon: RefreshCcw, color: '#8B5CF6', bg: 'from-[#8B5CF6]/15 to-transparent' },
  badge: { icon: Award, color: '#0F8A5F', bg: 'from-[#0F8A5F]/15 to-transparent' },
  user: { icon: User, color: '#6366F1', bg: 'from-[#6366F1]/15 to-transparent' },
  info: { icon: Info, color: '#71717A', bg: 'from-[#71717A]/15 to-transparent' },
  support_message: { icon: MessageCircle, color: '#00D387', bg: 'from-[#00D387]/15 to-transparent' },
  support_reply: { icon: MessageCircle, color: '#0F8A5F', bg: 'from-[#0F8A5F]/15 to-transparent' },
  company_pending: { icon: Shield, color: '#F59E0B', bg: 'from-[#F59E0B]/15 to-transparent' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Gerade eben';
  if (mins < 60) return `vor ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `vor ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `vor ${days}d`;
  return new Date(dateStr).toLocaleDateString('de-DE');
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [muted, setMuted] = useState(isNotificationMuted());
  const lastUnreadRef = useRef(0);
  const firstLoadRef = useRef(true);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications?limit=30');
      const nextUnread = data.unread_count || 0;
      // Play subtle sound if unread count increased (skip first load).
      if (!firstLoadRef.current && nextUnread > lastUnreadRef.current) {
        playNotificationSound();
      }
      firstLoadRef.current = false;
      lastUnreadRef.current = nextUnread;
      setNotifications(data.items || []);
      setUnreadCount(nextUnread);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  };

  const deleteNotif = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      fetchNotifications();
    } catch (e) { /* silent */ }
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell">
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell-btn"
        className="relative w-10 h-10 rounded-full flex items-center justify-center text-[#52525B] hover:text-primary hover:bg-[#F4F4F5] transition-all duration-200"
      >
        <Bell className="w-[18px] h-[18px]" />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-br from-[#00D387] to-[#0F8A5F] flex items-center justify-center shadow-[0_0_10px_rgba(0,211,135,0.45)] ring-2 ring-white"
            >
              <span className="text-[9.5px] font-bold text-white leading-none">{unreadCount > 9 ? '9+' : unreadCount}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.2, ease }}
            className="fixed left-2 right-2 top-[68px] w-auto sm:absolute sm:left-auto sm:right-0 sm:top-12 sm:w-[380px] max-h-[80vh] sm:max-h-[560px] flex flex-col rounded-2xl bg-white/95 backdrop-blur-2xl border border-[#E4E4E7] shadow-elevated z-50 overflow-hidden"
            data-testid="notification-panel"
          >
            {/* Header */}
            <div className="relative flex items-center justify-between px-5 py-4 bg-gradient-to-r from-[#E6FBF3]/60 via-white to-[#E6FBF3]/30 border-b border-[#F4F4F5]">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#00D387] to-[#0F8A5F] flex items-center justify-center shadow-soft">
                  <Bell className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-[13.5px] font-bold text-primary leading-none">Benachrichtigungen</h3>
                  <p className="text-[11px] text-[#71717A] mt-1">{unreadCount > 0 ? `${unreadCount} neue Nachrichten` : 'Alle gelesen'}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => { setNotificationMuted(!muted); setMuted(!muted); }}
                  data-testid="notify-mute-toggle"
                  title={muted ? 'Ton aktivieren' : 'Ton stumm schalten'}
                  className="p-1.5 text-[#71717A] hover:text-[#0F8A5F] hover:bg-white rounded-lg transition-colors">
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} data-testid="mark-all-read-btn"
                    className="p-1.5 text-[#71717A] hover:text-[#0F8A5F] hover:bg-white rounded-lg transition-colors" title="Alle als gelesen markieren">
                    <CheckCheck className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="p-1.5 text-[#71717A] hover:text-primary hover:bg-white rounded-lg transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#E6FBF3] to-white border border-[#E4E4E7] flex items-center justify-center mb-4">
                    <Bell className="w-5 h-5 text-[#0F8A5F]" />
                  </div>
                  <p className="text-[13px] font-semibold text-primary">Keine Benachrichtigungen</p>
                  <p className="text-[11.5px] text-[#71717A] mt-1">Sie sind auf dem neuesten Stand.</p>
                </div>
              ) : (
                notifications.map((notif, i) => {
                  const typeConfig = typeIcons[notif.type] || typeIcons.info;
                  const Icon = typeConfig.icon;
                  return (
                    <motion.div
                      key={notif.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.02, ease }}
                      className={`group relative px-5 py-4 border-b border-[#F4F4F5] hover:bg-[#0F8A5F]/[0.04] transition-all duration-200 ${
                        !notif.read ? 'bg-[#00D387]/[0.02]' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        {/* Icon */}
                        <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${typeConfig.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <Icon className="w-4 h-4" style={{ color: typeConfig.color }} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-[13px] leading-tight ${!notif.read ? 'font-bold text-primary' : 'font-medium text-[#8a8f8c]'}`}>
                              {notif.title}
                            </p>
                            {!notif.read && (
                              <div className="w-2 h-2 rounded-full bg-[#00D387] flex-shrink-0 mt-1.5 shadow-[0_0_6px_rgba(0,211,135,0.5)]" />
                            )}
                          </div>
                          <p className="text-[12px] text-[#71717A] mt-1 leading-[1.5] line-clamp-2">{notif.message}</p>
                          <p className="text-[10px] text-[#A1A1AA] mt-1.5">{timeAgo(notif.created_at)}</p>
                        </div>
                      </div>

                      {/* Hover Actions */}
                      <div className="absolute right-3 top-3 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        {!notif.read && (
                          <button onClick={(e) => { e.stopPropagation(); markRead(notif.id); }}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-[#71717A] hover:text-[#00D387] hover:bg-[#00D387]/10 transition-all"
                            title="Als gelesen markieren">
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); deleteNotif(notif.id); }}
                          className="w-7 h-7 rounded-full flex items-center justify-center text-[#71717A] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-all"
                          title="Löschen">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
